import React from 'react';
import PropTypes from 'prop-types';

/**
 * OmPagination - A pagination component using the same pattern as AuditLogs
 * 
 * @param {Object} props - Component props
 * @param {number} props.currentPage - Current active page (1-based)
 * @param {number} props.totalItems - Total number of items
 * @param {number} props.itemsPerPage - Items per page (default: 10)
 * @param {Function} props.onPageChange - Callback function called when page changes
 * @param {boolean} props.showResultsInfo - Show "Showing X to Y of Z results" (default: true)
 */
const OmPagination = ({ 
  currentPage = 1, 
  totalItems = 0,
  itemsPerPage = 10,
  onPageChange,
  showResultsInfo = true
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Don't render if there are no items or only one page
  if (totalItems <= itemsPerPage) {
    return null;
  }

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      onPageChange(newPage);
    }
  };

  // Calculate visible page numbers (show up to 5 pages)
  const getVisiblePages = () => {
    const maxVisible = 5;
    const pages = [];
    
    if (totalPages <= maxVisible) {
      // Show all pages if total is less than max
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show pages around current page
      let start = Math.max(1, currentPage - 2);
      let end = Math.min(totalPages, start + maxVisible - 1);
      
      // Adjust start if we're near the end
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  const visiblePages = getVisiblePages();

  return (
    <div className="bg-crystal-white px-4 py-3 flex items-center justify-between border-t border-silver-mist/30 sm:px-6">
      {/* Mobile Version */}
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="relative inline-flex items-center px-4 py-2 border border-silver-mist/50 text-sm font-medium rounded-lg text-graphite bg-crystal-white hover:bg-fresh-leaf/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        >
          Previous
        </button>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="ml-3 relative inline-flex items-center px-4 py-2 border border-silver-mist/50 text-sm font-medium rounded-lg text-graphite bg-crystal-white hover:bg-fresh-leaf/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        >
          Next
        </button>
      </div>

      {/* Desktop Version */}
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        {showResultsInfo && (
          <div>
            <p className="text-sm text-graphite">
              Showing{' '}
              <span className="font-medium text-deep-jungle">
                {((currentPage - 1) * itemsPerPage) + 1}
              </span>{' '}
              to{' '}
              <span className="font-medium text-deep-jungle">
                {Math.min(currentPage * itemsPerPage, totalItems)}
              </span>{' '}
              of{' '}
              <span className="font-medium text-deep-jungle">{totalItems}</span>{' '}
              results
            </p>
          </div>
        )}
        <div>
          <nav className="relative z-0 inline-flex rounded-lg shadow-sm overflow-hidden">
            {/* Previous Button */}
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-3 py-2 border border-silver-mist/50 bg-crystal-white text-sm font-medium text-steel hover:bg-fresh-leaf/10 hover:text-deep-jungle disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <span className="sr-only">Previous</span>
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>
            
            {/* Page Numbers */}
            {visiblePages.map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-colors duration-200 ${
                  pageNum === currentPage
                    ? 'z-10 bg-forest-fern border-forest-fern text-crystal-white shadow-sm'
                    : 'bg-crystal-white border-silver-mist/50 text-steel hover:bg-fresh-leaf/10 hover:text-deep-jungle hover:border-fresh-leaf/30'
                }`}
              >
                {pageNum}
              </button>
            ))}
            
            {/* Next Button */}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="relative inline-flex items-center px-3 py-2 border border-silver-mist/50 bg-crystal-white text-sm font-medium text-steel hover:bg-fresh-leaf/10 hover:text-deep-jungle disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <span className="sr-only">Next</span>
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

OmPagination.propTypes = {
  currentPage: PropTypes.number,
  totalItems: PropTypes.number.isRequired,
  itemsPerPage: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  showResultsInfo: PropTypes.bool,
};

export default OmPagination;